import { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { useSettings } from '../hooks/useSettings';
import { listImportMatches, resolveImportMatch, type ImportMatch } from '../lib/db';
import { recName } from '../types';

const REASON: Record<ImportMatch['candidates'][number]['reasons'][number], string> = {
  name: 'same name',
  similar_name: 'similar name',
  email: 'same email',
  phone: 'same phone',
};

/** Imports held for a human decision (administrators only). Loads on mount and whenever contacts change. */
export function useImportMatches(enabled: boolean) {
  const [matches, setMatches] = useState<ImportMatch[]>([]);
  const refresh = useCallback(async () => {
    if (!enabled) return;
    try { setMatches(await listImportMatches()); } catch { setMatches([]); }
  }, [enabled]);
  useEffect(() => {
    void refresh();
    window.addEventListener('compass:contacts-changed', refresh);
    return () => window.removeEventListener('compass:contacts-changed', refresh);
  }, [refresh]);
  return { matches, refresh };
}

export default function ImportMatchReview({
  matches,
  onClose,
  onDecided,
}: {
  matches: ImportMatch[];
  onClose: () => void;
  onDecided: () => void | Promise<void>;
}) {
  const { settings } = useSettings();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const d = (ids: string[]) => ids.map((id) => recName(settings.districts, id)).join(', ');

  const decide = async (m: ImportMatch, outcome: 'same_person' | 'different_person', personId?: string) => {
    setBusy(m.caseId);
    setErr(null);
    try {
      await resolveImportMatch({ caseId: m.caseId, outcome, personId });
      await onDecided();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that decision.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal title="Imported people who may already be in Compass" onClose={onClose}>
      <div className="subtitle" style={{ marginBottom: 8 }}>
        These people appeared in your my.Scouting.org import, and someone added manually looks similar. A matching name,
        email or phone never decides this on its own. If it is the same person, their Scouting Member ID is attached to the
        existing record and everything entered by hand is kept.
      </div>
      {matches.length === 0 && <div className="subtitle">Nothing left to review.</div>}
      {matches.map((m) => (
        <div key={m.caseId} style={{ borderTop: '1px solid var(--line)', padding: '10px 0' }}>
          <div>
            <b>{m.importedName}</b> <span className="subtitle">· from my.Scouting · Member ID {m.memberId}</span>
            <div className="subtitle">{[m.unitName, m.districtIds.length ? d(m.districtIds) : null].filter(Boolean).join(' · ')}</div>
          </div>
          {m.candidates.map((c) => (
            <div key={c.personId} className="checkbox-row" style={{ justifyContent: 'space-between', gap: 12, marginTop: 6 }}>
              <span>
                Added manually: <b>{c.displayName}</b>
                <div className="subtitle">
                  {c.reasons.map((r) => REASON[r]).join(', ')}
                  {c.districtIds.length > 0 && ` · ${d(c.districtIds)}`}
                </div>
              </span>
              <button className="btn" disabled={busy === m.caseId} onClick={() => decide(m, 'same_person', c.personId)}>
                Same person
              </button>
            </div>
          ))}
          <div className="actions" style={{ marginTop: 6 }}>
            <button className="btn" disabled={busy === m.caseId} onClick={() => decide(m, 'different_person')}>
              {m.candidates.length === 1 ? 'Different person' : 'None of these — different person'}
            </button>
          </div>
        </div>
      ))}
      {err && <div className="err">{err}</div>}
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
